/**
 * ImportTab — Import tab content for the PropBrowserModal.
 * Renders the textarea, parse button, match results display with line picker and override buttons.
 */

import type { BettableProp } from '@utils/propUtils';
import { formatPropType } from '@utils/propUtils';
import { AlertCircle, AlertTriangle, Check } from 'lucide-react';
import type { ChangeEvent, ReactElement } from 'react';

import { normalizeOU } from './propBrowserHelpers';
import type { MatchResult } from './propBrowserTypes';

export interface ImportTabProps {
  loading: boolean;
  availableProps: BettableProp[];
  importText: string;
  setImportText: (text: string) => void;
  matchResults: MatchResult[] | null;
  addedPropIds: Set<string>;
  overrides: Map<number, BettableProp>;
  handleParse: () => void;
  handleAddAllMatched: () => void;
  handleAddFromImport: (prop: BettableProp) => void;
  handleSelectOverride: (index: number, prop: BettableProp) => void;
  getEffectiveProp: (index: number, result: MatchResult) => BettableProp | null;
  checkBlocked: (prop: BettableProp) => { canAdd: boolean; reason?: string };
  matchedCount: number;
  addableCount: number;
}

/**
 * Deduplicate props by line value — prefer standard markets over alternates (better odds).
 */
function dedupeByLine(props: BettableProp[]): BettableProp[] {
  const byLine = new Map<number, BettableProp>();
  for (const p of props) {
    if (p.line == null) continue;
    const existing = byLine.get(p.line);
    if (!existing) {
      byLine.set(p.line, p);
    } else {
      // Prefer standard (non-alternate) over alternate for same line
      const existingIsAlt = existing.propType?.includes('_alternate');
      const currentIsAlt = p.propType?.includes('_alternate');
      if (existingIsAlt && !currentIsAlt) {
        byLine.set(p.line, p); // Replace alternate with standard
      }
    }
  }
  return Array.from(byLine.values()).sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
}

export function ImportTab({
  loading,
  availableProps,
  importText,
  setImportText,
  matchResults,
  addedPropIds,
  overrides,
  handleParse,
  handleAddAllMatched,
  handleAddFromImport,
  handleSelectOverride,
  getEffectiveProp,
  checkBlocked,
  matchedCount,
  addableCount,
}: ImportTabProps): ReactElement {
  return (
    <div className="space-y-4">
      {/* Loading state */}
      {loading && (
        <div className={'text-center py-8 text-theme-text-tertiary'}>
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-t-transparent mb-2 border-theme-primary" />
          <p className="text-sm">Loading available props...</p>
        </div>
      )}

      {/* Textarea */}
      {!loading && (
        <>
          <div>
            <textarea
              value={importText}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setImportText(e.target.value)}
              placeholder={`Paste your bets, one per line:

Player Props (pts/pt/points, reb, ast, 3pm, stl, blk):
LeBron Over 25.5 pts
Tatum O27.5 reb
Curry U5.5 ast
Edwards O2.5 3pm

Combo Props (pra, pr, pa, ra):
Jokic O45.5 PRA
Tatum O35.5 PR
LeBron O32.5 PA

Game Props:
Lakers -5.5
Celtics ML
Lakers vs Celtics O220.5`}
              className="w-full h-40 p-3 border rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none resize-none bg-theme-bg border-theme-border focus:border-theme-primary"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleParse}
              disabled={!importText.trim() || availableProps.length === 0}
              className="px-4 py-2 rounded-lg text-theme-on-primary-fill text-sm font-medium transition bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-gray-500"
            >
              Parse & Match
            </button>
            {matchResults && addableCount > 0 && (
              <button
                type="button"
                onClick={handleAddAllMatched}
                className="px-4 py-2 bg-theme-success hover:bg-theme-success rounded-lg text-white text-sm font-medium transition"
              >
                Add All Matched ({addableCount})
              </button>
            )}
          </div>

          {availableProps.length === 0 && !loading && (
            <p className={'text-sm text-theme-text-tertiary'}>
              No props available to match against. Props become available closer to game time.
            </p>
          )}

          {/* Results */}
          {matchResults && (
            <div className="space-y-2">
              {/* Summary with error count if any */}
              {matchedCount === matchResults.length ? (
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <Check className="w-4 h-4" />
                  All {matchResults.length} bets matched successfully
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-green-400">{matchedCount} matched</span>
                  <span className="text-gray-500">&bull;</span>
                  <span className="text-red-400">{matchResults.length - matchedCount} failed</span>
                </div>
              )}

              {matchResults.map((result, i) => {
                const { parsed, reason, alternatives } = result;
                const effectiveProp = getEffectiveProp(i, result);
                const isAdded = effectiveProp ? addedPropIds.has(effectiveProp.id) : false;
                const blocked = effectiveProp ? checkBlocked(effectiveProp) : { canAdd: false };
                const hasAlternatives = alternatives && alternatives.length > 0;
                const wasAdjusted = reason && effectiveProp && !overrides.has(i);

                // No match and no alternatives - show error
                if (!effectiveProp && !hasAlternatives) {
                  return (
                    <div key={i} className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">
                            {parsed.raw}
                          </div>
                          <div className="text-xs text-red-400 mt-1">
                            {reason || 'Could not match this bet to available props'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Get all options (current + alternatives) for line picker
                const allOptions = effectiveProp
                  ? [effectiveProp, ...(alternatives || [])].filter(
                      (p, idx, arr) => arr.findIndex((x) => x.id === p.id) === idx,
                    )
                  : alternatives || [];

                // Group by direction for display
                // Treat alternate props without overUnder as 'over' (40+ means Over 40)
                // Deduplicate by line value - prefer standard markets over alternates (better odds)
                const overOptions = dedupeByLine(
                  allOptions
                    .filter((p) => {
                      const ou = normalizeOU(p.overUnder);
                      // Include 'over' and props without direction (alternates default to over)
                      return ou === 'over' || (!ou && p.propType?.includes('_alternate'));
                    })
                    .sort((a, b) => (a.line ?? 0) - (b.line ?? 0)),
                );
                const underOptions = dedupeByLine(
                  allOptions
                    .filter((p) => normalizeOU(p.overUnder) === 'under')
                    .sort((a, b) => (a.line ?? 0) - (b.line ?? 0)),
                );

                // Matched (or has alternatives to choose from)
                return (
                  <div
                    key={i}
                    className={`p-3 rounded-lg border transition ${
                      isAdded
                        ? 'bg-gray-800/30 border-gray-700 opacity-50'
                        : wasAdjusted
                          ? 'bg-yellow-900/20 border-yellow-700/50'
                          : 'bg-green-900/20 border-green-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium">
                            {effectiveProp?.playerName || effectiveProp?.team || parsed.playerName}
                          </span>
                          {isAdded && (
                            <span className="text-[10px] bg-theme-surface-hover text-gray-300 px-1.5 py-0.5 rounded">
                              Added
                            </span>
                          )}
                          {overrides.has(i) && (
                            <span className="text-[10px] bg-theme-primary/30 text-blue-400 px-1.5 py-0.5 rounded">
                              Modified
                            </span>
                          )}
                        </div>
                        {effectiveProp && (
                          <div className="text-xs text-gray-400">
                            {effectiveProp.overUnder} {effectiveProp.line}{' '}
                            {formatPropType(effectiveProp.propType)}
                          </div>
                        )}
                        {wasAdjusted && (
                          <div className="text-xs text-yellow-400 mt-1">{reason}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {effectiveProp && (
                          <span className="text-green-400 text-sm">{effectiveProp.odds}</span>
                        )}
                        {effectiveProp && !isAdded && blocked.canAdd && (
                          <button
                            type="button"
                            onClick={() => handleAddFromImport(effectiveProp)}
                            className="px-2 py-1 bg-theme-success hover:bg-theme-success rounded text-xs text-white transition"
                          >
                            Add
                          </button>
                        )}
                        {effectiveProp && !isAdded && !blocked.canAdd && (
                          <div className="flex items-center gap-1.5 text-xs text-yellow-500">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span title={blocked.reason}>Conflicts</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Line/Direction Picker - show when there are multiple options to choose from */}
                    {!isAdded && overOptions.length + underOptions.length > 1 && (
                      <div className={'mt-2 pt-2 border-t border-theme-border/30'}>
                        <div className={'text-[10px] mb-1 text-theme-text-tertiary'}>
                          Pick a line:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {/* Over options */}
                          {overOptions.length > 0 && (
                            <div className="flex items-center gap-1">
                              <span className={'text-[10px] mr-0.5 text-theme-text-tertiary'}>
                                O
                              </span>
                              {overOptions.slice(0, 6).map((opt) => (
                                <button
                                  type="button"
                                  key={opt.id}
                                  onClick={() => handleSelectOverride(i, opt)}
                                  className={`px-1.5 py-0.5 text-[10px] rounded transition ${
                                    effectiveProp?.id === opt.id
                                      ? 'bg-theme-accent text-white'
                                      : 'bg-theme-surface text-gray-300 hover:bg-theme-surface-hover'
                                  }`}
                                >
                                  {opt.line}
                                </button>
                              ))}
                              {overOptions.length > 6 && (
                                <span className={'text-[10px] text-theme-text-tertiary'}>
                                  +{overOptions.length - 6}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Under options */}
                          {underOptions.length > 0 && (
                            <div className="flex items-center gap-1 ml-2">
                              <span className={'text-[10px] mr-0.5 text-theme-text-tertiary'}>
                                U
                              </span>
                              {underOptions.slice(0, 6).map((opt) => (
                                <button
                                  type="button"
                                  key={opt.id}
                                  onClick={() => handleSelectOverride(i, opt)}
                                  className={`px-1.5 py-0.5 text-[10px] rounded transition ${
                                    effectiveProp?.id === opt.id
                                      ? 'bg-theme-accent text-white'
                                      : 'bg-theme-surface text-gray-300 hover:bg-theme-surface-hover'
                                  }`}
                                >
                                  {opt.line}
                                </button>
                              ))}
                              {underOptions.length > 6 && (
                                <span className={'text-[10px] text-theme-text-tertiary'}>
                                  +{underOptions.length - 6}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
